import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserCustomerProfileThunk,
  updateUserCustomerProfileThunk,
} from "../../../store/thunks/userThunks";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Badge from "../../../components/common/Badge";
import {
  UserIcon,
  CalendarIcon,
  HeartIcon,
  ShoppingBagIcon,
  PlusCircleIcon,
  XCircleIcon,
  CheckCircleIcon,
  SparklesIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/outline";
import { notifySuccess, notifyError } from "../../../utils/notification";

const CustomerProfileManager = ({ isOpen, onClose, userId, userName }) => {
  const dispatch = useDispatch();
  const { customerProfile, loading } = useSelector((state) => state.user);
  const [activeTab, setActiveTab] = useState("occasions");
  const [isEditing, setIsEditing] = useState(false);

  console.log("Customer Profile:", customerProfile);

  // Form states
  const [newOccasion, setNewOccasion] = useState({
    type: "ENGAGEMENT",
    date: "",
    occasion: "",
    budget: { min: 0, max: 0 },
    forWhom: "SELF",
    notes: "",
  });

  const [preferences, setPreferences] = useState({
    metals: {
      gold: { interested: false, preferredKarat: [], preferredColor: [] },
      silver: { interested: false },
      platinum: { interested: false },
      diamond: { interested: false, preferredClarity: [], preferredCut: [] },
    },
    styles: [],
    weightRange: { min: 0, max: 0, unit: "grams" },
    priceRange: { min: 0, max: 0, currency: "INR" },
    favoriteCategories: [],
    gemstones: [],
  });

  useEffect(() => {
    if (isOpen && userId) {
      dispatch(getUserCustomerProfileThunk(userId));
    }
  }, [isOpen, userId, dispatch]);

  useEffect(() => {
    if (customerProfile?.preferences) {
      setPreferences(customerProfile.preferences);
    }
  }, [customerProfile]);

  const tabs = [
    { id: "occasions", label: "Occasions", icon: CalendarIcon },
    { id: "preferences", label: "Preferences", icon: HeartIcon },
    { id: "purchaseHistory", label: "Purchase History", icon: ShoppingBagIcon },
  ];

  const occasionTypes = [
    "ENGAGEMENT",
    "WEDDING",
    "ANNIVERSARY",
    "BIRTHDAY",
    "FESTIVAL",
    "GIFT",
    "INVESTMENT",
    "PERSONAL",
    "OTHER",
  ];

  const styleOptions = [
    "TRADITIONAL",
    "MODERN",
    "ANTIQUE",
    "MINIMALIST",
    "HEAVY",
    "LIGHT",
  ];

  const categoryOptions = [
    "NECKLACE",
    "EARRINGS",
    "RINGS",
    "BANGLES",
    "BRACELET",
    "PENDANT",
    "CHAIN",
  ];

  const handleAddOccasion = async () => {
    try {
      const updatedOccasions = [
        ...(customerProfile?.occasions || []),
        { ...newOccasion, date: new Date(newOccasion.date) },
      ];

      await dispatch(
        updateUserCustomerProfileThunk({
          userId,
          profileData: { occasions: updatedOccasions },
        })
      );

      notifySuccess("Occasion added successfully");
      setNewOccasion({
        type: "ENGAGEMENT",
        date: "",
        occasion: "",
        budget: { min: 0, max: 0 },
        forWhom: "SELF",
        notes: "",
      });
      setIsEditing(false);
    } catch (error) {
      notifyError("Failed to add occasion");
    }
  };

  const handleRemoveOccasion = async (index) => {
    const updatedOccasions = customerProfile.occasions.filter(
      (_, i) => i !== index
    );
    await dispatch(
      updateUserCustomerProfileThunk({
        userId,
        profileData: { occasions: updatedOccasions },
      })
    );
    notifySuccess("Occasion removed");
  };

  const handleSavePreferences = async () => {
    try {
      await dispatch(
        updateUserCustomerProfileThunk({
          userId,
          profileData: { preferences },
        })
      );
      notifySuccess("Preferences updated successfully");
    } catch (error) {
      notifyError("Failed to update preferences");
    }
  };

  const toggleArrayItem = (array, item) => {
    return array.includes(item)
      ? array.filter((i) => i !== item)
      : [...array, item];
  };

  // Render Occasions Tab
  const renderOccasions = () => (
    <div className="space-y-4">
      {/* Add New Occasion */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">Add New Occasion</h4>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? (
              <XCircleIcon className="w-4 h-4" />
            ) : (
              <PlusCircleIcon className="w-4 h-4" />
            )}
          </Button>
        </div>

        {isEditing && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Occasion Type
                </label>
                <select
                  value={newOccasion.type}
                  onChange={(e) =>
                    setNewOccasion({ ...newOccasion, type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {occasionTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                label="Date"
                type="date"
                value={newOccasion.date}
                onChange={(e) =>
                  setNewOccasion({ ...newOccasion, date: e.target.value })
                }
              />

              <Input
                label="Occasion Name"
                value={newOccasion.occasion}
                onChange={(e) =>
                  setNewOccasion({ ...newOccasion, occasion: e.target.value })
                }
                placeholder="e.g., Son's Wedding"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  For Whom
                </label>
                <select
                  value={newOccasion.forWhom}
                  onChange={(e) =>
                    setNewOccasion({ ...newOccasion, forWhom: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {["SELF", "SPOUSE", "PARENT", "CHILD", "FRIEND", "OTHER"].map(
                    (who) => (
                      <option key={who} value={who}>
                        {who}
                      </option>
                    )
                  )}
                </select>
              </div>

              <Input
                label="Min Budget (₹)"
                type="number"
                value={newOccasion.budget.min}
                onChange={(e) =>
                  setNewOccasion({
                    ...newOccasion,
                    budget: {
                      ...newOccasion.budget,
                      min: Number(e.target.value),
                    },
                  })
                }
              />

              <Input
                label="Max Budget (₹)"
                type="number"
                value={newOccasion.budget.max}
                onChange={(e) =>
                  setNewOccasion({
                    ...newOccasion,
                    budget: {
                      ...newOccasion.budget,
                      max: Number(e.target.value),
                    },
                  })
                }
              />
            </div>

            <Input
              label="Notes"
              value={newOccasion.notes}
              onChange={(e) =>
                setNewOccasion({ ...newOccasion, notes: e.target.value })
              }
              placeholder="Additional details..."
            />

            <Button variant="primary" onClick={handleAddOccasion}>
              Add Occasion
            </Button>
          </div>
        )}
      </div>

      {/* Existing Occasions */}
      <div className="space-y-3">
        {customerProfile?.occasions?.map((occasion, index) => (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-lg p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge
                    variant={occasion.purchased ? "success" : "warning"}
                    size="sm"
                  >
                    {occasion.type.replace("_", " ")}
                  </Badge>
                  {occasion.purchased && (
                    <CheckCircleIcon className="w-4 h-4 text-green-600" />
                  )}
                </div>
                <h5 className="font-semibold text-gray-900">
                  {occasion.occasion}
                </h5>
                <p className="text-sm text-gray-600 mt-1">
                  Date: {new Date(occasion.date).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600">For: {occasion.forWhom}</p>
                {occasion.budget && (
                  <p className="text-sm text-gray-600">
                    Budget: ₹{occasion.budget.min?.toLocaleString()} - ₹
                    {occasion.budget.max?.toLocaleString()}
                  </p>
                )}
                {occasion.notes && (
                  <p className="text-sm text-gray-500 mt-2">{occasion.notes}</p>
                )}
              </div>
              <button
                onClick={() => handleRemoveOccasion(index)}
                className="text-red-500 hover:text-red-700"
              >
                <XCircleIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}

        {(!customerProfile?.occasions ||
          customerProfile.occasions.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            <CalendarIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No occasions tracked yet</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render Preferences Tab
  const renderPreferences = () => (
    <div className="space-y-6">
      {/* Metal Preferences */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-yellow-600" />
          Metal Preferences
        </h4>

        <div className="space-y-4">
          {/* Gold */}
          <div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={preferences.metals.gold.interested}
                onChange={(e) =>
                  setPreferences({
                    ...preferences,
                    metals: {
                      ...preferences.metals,
                      gold: {
                        ...preferences.metals.gold,
                        interested: e.target.checked,
                      },
                    },
                  })
                }
                className="rounded"
              />
              <span className="font-medium">Gold</span>
            </label>

            {preferences.metals.gold.interested && (
              <div className="ml-6 space-y-2">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Preferred Karat:</p>
                  <div className="flex flex-wrap gap-2">
                    {["18K", "22K", "24K"].map((karat) => (
                      <button
                        key={karat}
                        onClick={() =>
                          setPreferences({
                            ...preferences,
                            metals: {
                              ...preferences.metals,
                              gold: {
                                ...preferences.metals.gold,
                                preferredKarat: toggleArrayItem(
                                  preferences.metals.gold.preferredKarat,
                                  karat
                                ),
                              },
                            },
                          })
                        }
                        className={`px-3 py-1 rounded-full text-sm ${
                          preferences.metals.gold.preferredKarat.includes(karat)
                            ? "bg-yellow-500 text-white"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {karat}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Preferred Color:</p>
                  <div className="flex flex-wrap gap-2">
                    {["YELLOW", "WHITE", "ROSE"].map((color) => (
                      <button
                        key={color}
                        onClick={() =>
                          setPreferences({
                            ...preferences,
                            metals: {
                              ...preferences.metals,
                              gold: {
                                ...preferences.metals.gold,
                                preferredColor: toggleArrayItem(
                                  preferences.metals.gold.preferredColor,
                                  color
                                ),
                              },
                            },
                          })
                        }
                        className={`px-3 py-1 rounded-full text-sm ${
                          preferences.metals.gold.preferredColor.includes(color)
                            ? "bg-yellow-500 text-white"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Other Metals */}
          {["silver", "platinum", "diamond"].map((metal) => (
            <label key={metal} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={preferences.metals[metal].interested}
                onChange={(e) =>
                  setPreferences({
                    ...preferences,
                    metals: {
                      ...preferences.metals,
                      [metal]: {
                        ...preferences.metals[metal],
                        interested: e.target.checked,
                      },
                    },
                  })
                }
                className="rounded"
              />
              <span className="font-medium capitalize">{metal}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Style Preferences */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-3">Style Preferences</h4>
        <div className="flex flex-wrap gap-2">
          {styleOptions.map((style) => (
            <button
              key={style}
              onClick={() =>
                setPreferences({
                  ...preferences,
                  styles: toggleArrayItem(preferences.styles, style),
                })
              }
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                preferences.styles.includes(style)
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {style}
            </button>
          ))}
        </div>
      </div>

      {/* Favorite Categories */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-3">
          Favorite Categories
        </h4>
        <div className="flex flex-wrap gap-2">
          {categoryOptions.map((category) => (
            <button
              key={category}
              onClick={() =>
                setPreferences({
                  ...preferences,
                  favoriteCategories: toggleArrayItem(
                    preferences.favoriteCategories,
                    category
                  ),
                })
              }
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                preferences.favoriteCategories.includes(category)
                  ? "bg-purple-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Budget Range */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <CurrencyRupeeIcon className="w-5 h-5 text-green-600" />
          Price Range
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Min (₹)"
            type="number"
            value={preferences.priceRange.min}
            onChange={(e) =>
              setPreferences({
                ...preferences,
                priceRange: {
                  ...preferences.priceRange,
                  min: Number(e.target.value),
                },
              })
            }
          />
          <Input
            label="Max (₹)"
            type="number"
            value={preferences.priceRange.max}
            onChange={(e) =>
              setPreferences({
                ...preferences,
                priceRange: {
                  ...preferences.priceRange,
                  max: Number(e.target.value),
                },
              })
            }
          />
        </div>
      </div>

      <Button
        variant="primary"
        onClick={handleSavePreferences}
        className="w-full"
      >
        Save Preferences
      </Button>
    </div>
  );

  // Render Purchase History Tab
  const renderPurchaseHistory = () => {
    const history = customerProfile?.purchaseHistory;

    if (!history || history.totalPurchases === 0) {
      return (
        <div className="text-center py-12">
          <ShoppingBagIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No purchase history available</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Purchases</p>
            <p className="text-2xl font-bold">{history.totalPurchases}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Value</p>
            <p className="text-2xl font-bold text-green-600">
              ₹{history.totalValue?.toLocaleString()}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Average Ticket</p>
            <p className="text-2xl font-bold text-blue-600">
              ₹{history.averageTicketSize?.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Category Breakdown */}
        {history.categoryPurchases?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">
              Category Breakdown
            </h4>
            <div className="space-y-2">
              {history.categoryPurchases.map((cat, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-700">{cat.category}</span>
                  <div className="text-right">
                    <span className="font-medium">{cat.count} items</span>
                    <span className="text-sm text-gray-500 ml-3">
                      ₹{cat.totalValue?.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Modal
        isOpen={isOpen}
        closeModal={onClose}
        title={`Customer Profile - ${userName}`}
        isFullScreen={true}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading profile...</p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={
        <div className="flex items-center gap-3">
          <UserIcon className="w-6 h-6 text-primary" />
          <span>Customer Profile - {userName}</span>
        </div>
      }
      isFullScreen={true}
    >
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex space-x-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-primary text-primary bg-primary-light/10"
                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-h-[600px] overflow-y-auto">
        {activeTab === "occasions" && renderOccasions()}
        {activeTab === "preferences" && renderPreferences()}
        {activeTab === "purchaseHistory" && renderPurchaseHistory()}
      </div>
    </Modal>
  );
};

export default CustomerProfileManager;
